import { ChevronLeft, ChevronRight, History, Plus, Search } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { HistoryModal } from '@/components/planning/HistoryModal'
import { currentMonthKey, monthRangeLabel, monthShort, monthYear, shiftMonth } from '@/components/planning/month'
import { MonthColumns } from '@/components/planning/MonthColumns'
import { type OpportunitySort, OpportunityList, SORT_OPTIONS } from '@/components/planning/OpportunityList'
import { OpportunityModal } from '@/components/planning/OpportunityModal'
import { filterOpportunities, matchesOpportunity } from '@/components/planning/opportunity-search'
import { StageFilter } from '@/components/planning/StageFilter'
import { countsIn, readStages } from '@/components/planning/stages'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { planningApi, usePlanningMutation, usePlanningOverview } from '@/lib/planning-api'
import type { MonthKey, Opportunity, OpportunityInput, OpportunityStage, OpportunityStatus, PlanningStage } from '@/lib/planning-types'

type Horizon = '3' | '6' | '12'

const SORT_STORAGE_KEY = 'vooruitblik.kansen-sortering'
const STAGE_STORAGE_KEY = 'vooruitblik.zekerheidstrappen'
const MONEY_STORAGE_KEY = 'vooruitblik.omzet-open'

export function VooruitblikPage() {
  const [from, setFrom] = useState<MonthKey>(currentMonthKey())
  const [horizon, setHorizon] = useState<Horizon>('3')
  const [editing, setEditing] = useState<Opportunity | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [openOpportunity, setOpenOpportunity] = useState<string | null>(null)
  // Zoeken is een handeling van dit moment en geen kijkvoorkeur: hij hoort niet
  // opgeslagen te worden, anders kom je morgen terug op een halve lijst.
  const [search, setSearch] = useState('')
  // Hoe je de kansen wilt zien is een kijkvoorkeur, geen eigenschap van de
  // planning: hij blijft op deze machine staan en geldt niet voor de rest.
  const [sort, setSort] = useState<OpportunitySort>(
    () => (SORT_OPTIONS.find((option) => option.value === localStorage.getItem(SORT_STORAGE_KEY))?.value ?? 'manual'),
  )
  // Hoeveel zekerheid je wilt meetellen is net zo goed een kijkvoorkeur als de
  // sortering: wie speculatief werk nooit meerekent hoort dat niet elke ochtend
  // opnieuw te moeten zeggen. Het verandert wel de cijfers en niet alleen de
  // volgorde, dus de knoppenrij staat permanent boven de kolommen en een verbleekte
  // trap is van een aanstaande te onderscheiden; zonder dat zou een bewaarde keuze
  // als een lege agenda kunnen lezen.
  const [stages, setStages] = useState<PlanningStage[]>(() => readStages(localStorage.getItem(STAGE_STORAGE_KEY)))
  // Of het persoonspaneel de omzet uitsplitst. Het staat hier en niet in het paneel
  // zelf, want anders klapt het dicht zodra je een andere persoon of maand aanklikt
  // en is het geen voorkeur meer maar een handeling die je blijft herhalen.
  const [moneyOpen, setMoneyOpen] = useState(() => localStorage.getItem(MONEY_STORAGE_KEY) === '1')
  const opportunitiesRef = useRef<HTMLDivElement>(null)

  const overview = usePlanningOverview(from, Number(horizon), stages)

  const create = usePlanningMutation((input: OpportunityInput) => planningApi.createOpportunity(input))
  const update = usePlanningMutation((id: string, input: OpportunityInput) => planningApi.updateOpportunity(id, input))
  const status = usePlanningMutation((id: string, next: OpportunityStatus, clientId: string | null, projectId: string | null) =>
    planningApi.setStatus(id, next, clientId, projectId),
  )
  const remove = usePlanningMutation((id: string) => planningApi.deleteOpportunity(id))
  const reorder = usePlanningMutation((ids: string[]) => planningApi.reorderOpportunities(ids))
  const allocation = usePlanningMutation((id: string, personId: string | null, month: MonthKey, hours: string) =>
    planningApi.setAllocation(id, personId, month, hours),
  )
  // Een reeks meldt zich wel, anders is een handeling die zes maanden raakt niet
  // van een enkele cel te onderscheiden.
  const allocationRange = usePlanningMutation(
    (id: string, rows: Parameters<typeof planningApi.setAllocations>[1]) => planningApi.setAllocations(id, rows),
    () => 'Reeks ingevuld',
  )
  const revenue = usePlanningMutation((id: string, month: MonthKey, amount: string) => planningApi.setRevenue(id, month, amount))
  // Een verschuiving raakt elke maand van de kans tegelijk; de melding zegt daarom
  // waar hij nu loopt, want dat is precies wat je wilde controleren.
  const shift = usePlanningMutation(
    (id: string, months: number) => planningApi.shiftOpportunity(id, months),
    (result) =>
      result.span.first === null
        ? 'Kans verschoven'
        : `Verschoven: ${monthRangeLabel([result.span.first, result.span.last ?? result.span.first])}`,
  )
  const capacity = usePlanningMutation((personId: string, month: MonthKey, hours: string | null) =>
    planningApi.setCapacity(personId, month, hours),
  )
  const applyHistory = usePlanningMutation(
    (rows: Parameters<typeof planningApi.applyHistorySuggestion>[0]) => planningApi.applyHistorySuggestion(rows),
    (result) =>
      `${result.applied} regel${result.applied === 1 ? '' : 's'} overgenomen${result.createdOpportunities > 0 ? `, ${result.createdOpportunities} kans aangemaakt` : ''}`,
  )

  const data = overview.data
  const filtering = search.trim() !== ''
  const visible = useMemo(() => filterOpportunities(data?.opportunities ?? [], search), [data?.opportunities, search])
  // De kansenlijst toont altijd alles, ook wat je even niet meetelt: anders is een
  // kans die je uitzet niet meer te bewerken. Hoeveel dat er zijn hoort de lijst
  // wel te zeggen, anders lijkt hij de cijfers erboven tegen te spreken.
  const excluded = useMemo(
    () => (data?.opportunities ?? []).filter((opportunity) => !countsIn(opportunity, stages)).length,
    [data?.opportunities, stages],
  )
  const atNow = from === currentMonthKey()

  const openEditor = (opportunity: Opportunity | null) => {
    setEditing(opportunity)
    setEditorOpen(true)
  }

  const toggleMoney = () => {
    const next = !moneyOpen
    setMoneyOpen(next)
    localStorage.setItem(MONEY_STORAGE_KEY, next ? '1' : '0')
  }

  /**
   * Naam, koppeling en status horen bij hetzelfde opslaan. Ze gaan wel
   * achtereenvolgens naar de server: de statusovergang is een eigen gebeurtenis
   * in de audit trail en mag de rest niet meeslepen.
   */
  const save = async (input: OpportunityInput, nextStatus: OpportunityStatus) => {
    setEditorOpen(false)
    const id = editing ? editing.id : (await create.mutateAsync([input])).id
    if (editing) await update.mutateAsync([editing.id, input])
    if ((editing?.status ?? 'ACTIVE') !== nextStatus) {
      await status.mutateAsync([id, nextStatus, input.clientId ?? null, input.projectId ?? null])
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Vooruitblik"
        description="Wat er aan werk aankomt, en of er ruimte voor is."
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryOpen(true)}>
              <History className="h-4 w-4" strokeWidth={1.85} />
              Vul uit historie
            </button>
            <button type="button" className="btn btn-primary" onClick={() => openEditor(null)}>
              <Plus className="h-4 w-4" strokeWidth={1.85} />
              Kans
            </button>
          </>
        }
      />

      {overview.isError ? <ErrorState error={overview.error} onRetry={() => void overview.refetch()} /> : null}

      <Card inset>
        <CardHeader
          className="flex-wrap gap-y-3 border-b border-hairline px-5 py-4"
          title={data ? monthRangeLabel(data.months) : 'Vooruitblik'}
          subtitle={data ? `Omzet die meetelt: ${formatEuros(toNumber(data.revenue))}` : undefined}
          action={
            /*
             * De kaartkop zet zijn actieblok op zijn eigen breedte, dus `w-full`
             * houdt hier niets tegen: zonder grens schuift de balk op een telefoon
             * onder de kaartrand door. Onder `sm` is er geen zijbalk, dus is het
             * scherm min de paginamarge en de kaartmarge de echte ruimte. De
             * horizonkeuze schuift daarbinnen zelf horizontaal.
             */
            <div className="flex w-full max-w-[calc(100vw-4.5rem)] items-center justify-between gap-2 sm:w-auto sm:max-w-none sm:justify-end">
              {/* Wijkt als eerste bij krappe breedte: hij schuift zelf horizontaal,
                  en de maandstepper ernaast valt niet weg te schuiven. */}
              <SegmentedControl<Horizon>
                className="min-w-0"
                label="Vooruitblik"
                value={horizon}
                onChange={setHorizon}
                segments={[
                  { value: '3', label: '3 mnd' },
                  { value: '6', label: '6 mnd' },
                  { value: '12', label: '12 mnd' },
                ]}
              />
              {/*
                Dezelfde vorm als de weekstepper in de urenbalk: een omlijnde pil
                met de periode ertussen, en de sprong terug naar nu ernaast. Drie
                kale spookknoppen lazen als versiering, en juist het terugkijken
                bleef daardoor onopgemerkt. De maand in het midden maakt van twee
                pijlen een stepper; de kaartkop links houdt het hele bereik.
              */}
              <div className="flex shrink-0 items-center gap-1">
                <div className="inline-flex items-center rounded-lg border border-hairline bg-surface p-0.5">
                  <button
                    type="button"
                    aria-label="Een maand terug"
                    title="Een maand terug"
                    className="btn btn-ghost h-7 w-7 p-0"
                    onClick={() => setFrom((month) => shiftMonth(month, -1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.85} />
                  </button>
                  {/* Op een telefoon staat het bereik als kaarttitel vlak erboven, en
                      daar is de breedte te kostbaar om het te herhalen. */}
                  <span className="hidden min-w-[34px] px-1 text-center text-xs text-ink-soft sm:block">
                    {monthShort(from, monthYear(currentMonthKey()))}
                  </span>
                  <button
                    type="button"
                    aria-label="Een maand vooruit"
                    title="Een maand vooruit"
                    className="btn btn-ghost h-7 w-7 p-0"
                    onClick={() => setFrom((month) => shiftMonth(month, 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.85} />
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost h-8 px-2 text-xs"
                  disabled={atNow}
                  title={atNow ? 'Je kijkt al vanaf deze maand' : 'Terug naar deze maand'}
                  onClick={() => setFrom(currentMonthKey())}
                >
                  Nu
                </button>
              </div>
            </div>
          }
        />
        <StageFilter
          value={stages}
          onChange={(next) => {
            setStages(next)
            localStorage.setItem(STAGE_STORAGE_KEY, next.join(','))
          }}
        />

        {data ? (
          <MonthColumns
            overview={data}
            moneyOpen={moneyOpen}
            onToggleMoney={toggleMoney}
            onSetCapacity={(personId, month, hours) => capacity.mutate([personId, month, hours])}
            // Vanuit het persoonspaneel doorklikken naar de kans: het paneel leest
            // alleen af, handelen doe je in de lijst eronder. Het paneel toont wel
            // de hele maand, dus wijst het naar een kans die je net weggefilterd
            // hebt, dan wijkt het filter: een doorgang hoort ergens uit te komen.
            onOpenOpportunity={(id) => {
              const target = data.opportunities.find((opportunity) => opportunity.id === id)
              if (target && !matchesOpportunity(target, search)) setSearch('')
              setOpenOpportunity(id)
              opportunitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }}
          />
        ) : (
          <div className="space-y-2 px-5 py-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        )}
      </Card>

      <Card inset ref={opportunitiesRef}>
        <CardHeader
          className="flex-wrap gap-y-3 border-b border-hairline px-5 py-4"
          title="Kansen"
          // Twee dingen kunnen de lijst en de cijfers erboven uit elkaar laten
          // lopen: een zoekterm verkleint de lijst terwijl de maanden alles blijven
          // tellen, en een uitgezette trap doet precies het omgekeerde. Allebei
          // horen ze hier te staan, want dit is de plek waar je het verschil ziet.
          subtitle={data ? listSubtitle(visible.length, data.opportunities.length, filtering, excluded) : undefined}
          action={
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
              <div className="flex h-8 w-full items-center gap-2 rounded-lg border border-hairline bg-surface px-2.5 text-xs text-ink-faint sm:w-auto">
                <Search className="h-4 w-4 shrink-0" />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Zoek op kans, klant of project"
                  className="w-full min-w-0 bg-transparent text-ink outline-hidden placeholder:text-ink-faint sm:w-48"
                />
              </div>
              <Select
                value={sort}
                onValueChange={(value) => {
                  setSort(value as OpportunitySort)
                  localStorage.setItem(SORT_STORAGE_KEY, value)
                }}
                options={SORT_OPTIONS}
                className="h-8 w-[180px] shrink-0 text-xs"
              />
            </div>
          }
        />
        {data ? (
          <OpportunityList
            overview={data}
            rows={visible}
            filtering={filtering}
            stages={stages}
            open={openOpportunity}
            onOpenChange={setOpenOpportunity}
            sort={sort}
            onManualOrder={() => {
              setSort('manual')
              localStorage.setItem(SORT_STORAGE_KEY, 'manual')
            }}
            handlers={{
              onSetAllocation: (id, personId, month, hours) => allocation.mutate([id, personId, month, hours]),
              onSetAllocations: (id, rows) => allocationRange.mutate([id, rows]),
              onSetRevenue: (id, month, amount) => revenue.mutate([id, month, amount]),
              onShift: (id, months) => shift.mutate([id, months]),
              onSetStage: (id, stage: OpportunityStage) => update.mutate([id, { stage }]),
              onEdit: openEditor,
              onNew: () => openEditor(null),
              onRestore: (id) => status.mutate([id, 'ACTIVE', null, null]),
              onReorder: (ids) => reorder.mutate([ids]),
            }}
          />
        ) : (
          <div className="space-y-2 px-5 py-4">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}
      </Card>

      <OpportunityModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        opportunity={editing}
        saving={create.isPending || update.isPending}
        onSave={(input, nextStatus) => void save(input, nextStatus)}
        onDelete={() => {
          if (!editing) return
          remove.mutate([editing.id])
          setEditorOpen(false)
        }}
      />

      <HistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        month={from}
        applying={applyHistory.isPending}
        onApply={(rows) => {
          applyHistory.mutate([rows])
          setHistoryOpen(false)
        }}
      />
    </div>
  )
}

/**
 * Waarin de lijst afwijkt van de maanden erboven, in een regel. Zonder afwijking
 * staat er geen boekhouding maar de gewone uitnodiging: een scherm dat niets te
 * melden heeft hoort te zwijgen.
 */
function listSubtitle(shown: number, total: number, filtering: boolean, excluded: number): string {
  const parts = []
  if (filtering) parts.push(`${shown} van ${total} kansen`)
  if (excluded > 0) {
    parts.push(`${excluded} ${excluded === 1 ? 'kans telt' : 'kansen tellen'} niet mee in de maanden hierboven`)
  } else if (filtering) {
    parts.push('de maanden hierboven tellen ze allemaal')
  }

  return parts.length > 0 ? parts.join(' · ') : 'Klik een kans open om de uren per maand te zetten'
}
